import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import { GlobalContext } from '../../../../store/GlobalContext';
import { resolveCategoryImage } from '../../../../data/productsApi';

type Category = {
  _id?: string;
  name: string;
  description?: string;
  imageUrl?: string;
  showOnHome?: boolean;
  displayOrder?: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
};

type CategoryListResponse = {
  categories: Category[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

type CategoryImpact = {
  categoryId: string;
  categoryName: string;
  productCount: number;
  products: Array<{ _id: string; name: string; isActive: boolean }>;
};

const Categories = () => {
  const { state } = useContext(GlobalContext);
  const token = state?.auth?.token;

  const [rows, setRows] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortBy, setSortBy] = useState<'createdAt' | 'name' | 'updatedAt' | 'displayOrder'>('displayOrder');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    showOnHome: false,
    displayOrder: '0',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [confirmAction, setConfirmAction] = useState<'delete' | 'status' | null>(null);
  const [confirmCategory, setConfirmCategory] = useState<Category | null>(null);
  const [confirmTargetStatus, setConfirmTargetStatus] = useState<boolean | null>(null);
  const [impact, setImpact] = useState<CategoryImpact | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const canGoPrev = page > 1;
  const canGoNext = totalPages > 0 && page < totalPages;

  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      page,
      limit,
      sortBy,
      sortOrder,
    };

    if (search.trim()) {
      params.search = search.trim();
    }

    if (statusFilter !== 'all') {
      params.isActive = statusFilter === 'active';
    }

    return params;
  }, [page, limit, search, statusFilter, sortBy, sortOrder]);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/categories', {
        params: queryParams,
      });

      const data: CategoryListResponse = response.data;
      const categories = Array.isArray(data?.categories)
        ? data.categories
        : Array.isArray(response.data)
          ? response.data
          : [];

      setRows(categories);
      setTotal(typeof data?.total === 'number' ? data.total : categories.length);
      setTotalPages(typeof data?.totalPages === 'number' ? data.totalPages : 0);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load categories.');
      setRows([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (!error && !success) return;
    const timer = setTimeout(() => {
      setError('');
      setSuccess('');
    }, 3000);
    return () => clearTimeout(timer);
  }, [error, success]);

  const resetDialogState = () => {
    setEditingCategory(null);
    setForm({ name: '', description: '', showOnHome: false, displayOrder: '0' });
    setImageFile(null);
  };

  const openCreateDialog = () => {
    setError('');
    setSuccess('');
    resetDialogState();
    setShowDialog(true);
  };

  const openEditDialog = (category: Category) => {
    setError('');
    setSuccess('');
    setEditingCategory(category);
    setForm({
      name: category.name || '',
      description: category.description || '',
      showOnHome: Boolean(category.showOnHome),
      displayOrder: String(category.displayOrder ?? 0),
    });
    setImageFile(null);
    setShowDialog(true);
  };

  const openImpactDialog = async (category: Category, action: 'delete' | 'status', targetStatus?: boolean) => {
    if (!category?._id) {
      return;
    }

    setError('');
    setSuccess('');
    setConfirmLoading(true);

    try {
      const response = await axios.get(`/categories/${category._id}/impact`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      setImpact(response.data);
      setConfirmAction(action);
      setConfirmCategory(category);
      setConfirmTargetStatus(typeof targetStatus === 'boolean' ? targetStatus : null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch impact details.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const closeImpactDialog = () => {
    setConfirmAction(null);
    setConfirmCategory(null);
    setConfirmTargetStatus(null);
    setImpact(null);
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!form.name.trim()) {
      setError('Category name is required.');
      return;
    }

    if (!token) {
      setError('Authentication is required. Please login again.');
      return;
    }

    try {
      const payload = new FormData();
      payload.append('name', form.name.trim());
      payload.append('description', form.description.trim());
      payload.append('showOnHome', String(form.showOnHome));
      payload.append('displayOrder', String(Number(form.displayOrder) || 0));
      if (imageFile) {
        payload.append('image', imageFile);
      }

      if (editingCategory?._id) {
        await axios.put(
          `/categories/${editingCategory._id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setSuccess('Category updated successfully.');
      } else {
        await axios.post(
          '/categories',
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setSuccess('Category created successfully.');
      }

      setShowDialog(false);
      resetDialogState();
      await fetchCategories();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save category.');
    }
  };

  const handleDelete = async (category: Category) => {
    if (!confirmCategory?._id || !token) {
      return;
    }

    setError('');
    setSuccess('');
    try {
      await axios.delete(`/categories/${confirmCategory._id}`, {
        data: { cascadeProducts: true },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Category deleted successfully.');
      closeImpactDialog();

      if (rows.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
      } else {
        await fetchCategories();
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete category.');
    }
  };

  const handleStatusChange = async () => {
    if (!confirmCategory?._id || typeof confirmTargetStatus !== 'boolean' || !token) {
      return;
    }

    setError('');
    setSuccess('');
    try {
      await axios.patch(
        `/categories/${confirmCategory._id}/status`,
        {
          isActive: confirmTargetStatus,
          applyToProducts: true,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setSuccess(confirmTargetStatus ? 'Category and related products activated.' : 'Category and related products deactivated.');
      closeImpactDialog();
      await fetchCategories();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update category status.');
    }
  };

  return (
    <>
      <Card className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Categories</h4>
          <Button variant="dark" onClick={openCreateDialog} data-testid="category-add-btn">
            Add Category
          </Button>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Form onSubmit={handleSearchSubmit} className="d-flex gap-2 flex-grow-1" style={{ minWidth: '260px' }}>
            <Form.Control
              placeholder="Search by name or description"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              aria-label="Search categories"
            />
            <Button type="submit" variant="outline-dark" data-testid="category-search-btn">Search</Button>
            <Button type="button" variant="outline-secondary" onClick={handleClearSearch}>Clear</Button>
          </Form>

          <Form.Select
            value={statusFilter}
            onChange={(event) => {
              setPage(1);
              setStatusFilter(event.target.value as 'all' | 'active' | 'inactive');
            }}
            aria-label="Filter by status"
            style={{ maxWidth: '180px' }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Form.Select>

          <Form.Select
            value={sortBy}
            onChange={(event) => {
              setPage(1);
              setSortBy(event.target.value as 'createdAt' | 'name' | 'updatedAt' | 'displayOrder');
            }}
            aria-label="Sort by"
            style={{ maxWidth: '180px' }}
          >
            <option value="displayOrder">Sort: Display Order</option>
            <option value="createdAt">Sort: Created Date</option>
            <option value="updatedAt">Sort: Updated Date</option>
            <option value="name">Sort: Name</option>
          </Form.Select>

          <Form.Select
            value={sortOrder}
            onChange={(event) => {
              setPage(1);
              setSortOrder(event.target.value as 'asc' | 'desc');
            }}
            aria-label="Sort order"
            style={{ maxWidth: '140px' }}
          >
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </Form.Select>

          <Form.Select
            value={limit}
            onChange={(event) => {
              setPage(1);
              setLimit(Number(event.target.value));
            }}
            aria-label="Rows per page"
            style={{ maxWidth: '140px' }}
          >
            <option value={5}>5 / page</option>
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
          </Form.Select>
        </div>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Order</th>
              <th>Name</th>
              <th>Description</th>
              <th>Home</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center">No categories found.</td>
              </tr>
            ) : (
              rows.map((category) => (
                <tr key={category._id ?? category.name}>
                  <td>{category.displayOrder ?? 0}</td>
                  <td>{category.name}</td>
                  <td>{category.description || '-'}</td>
                  <td>{category.showOnHome ? <Badge bg="info">Shown</Badge> : <Badge bg="light" text="dark">Hidden</Badge>}</td>
                  <td>
                    {category.isActive ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>}
                  </td>
                  <td>
                    <Button size="sm" variant="warning" className="me-2" onClick={() => openEditDialog(category)}>
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant={category.isActive ? 'secondary' : 'success'}
                      className="me-2"
                      onClick={() => openImpactDialog(category, 'status', !category.isActive)}
                    >
                      {category.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => openImpactDialog(category, 'delete')}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <div className="d-flex justify-content-between align-items-center">
          <small>
            Showing {rows.length} of {total} categories
          </small>
          <div className="d-flex align-items-center gap-2">
            <Button variant="outline-secondary" size="sm" disabled={!canGoPrev} onClick={() => setPage((prev) => prev - 1)}>
              Prev
            </Button>
            <span data-testid="category-page-indicator">Page {page}{totalPages > 0 ? ` of ${totalPages}` : ''}</span>
            <Button variant="outline-secondary" size="sm" disabled={!canGoNext} onClick={() => setPage((prev) => prev + 1)}>
              Next
            </Button>
          </div>
        </div>
      </Card>

      <Modal show={showDialog} onHide={() => { setShowDialog(false); resetDialogState(); }}>
        <Modal.Header closeButton>
          <Modal.Title>{editingCategory ? 'Edit Category' : 'Add Category'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSave}>
            <Form.Group className="mb-2" controlId="categoryNameInput">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                required
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="categoryDescriptionInput">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="categoryDisplayOrderInput">
              <Form.Label>Display Order</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={form.displayOrder}
                onChange={(event) => setForm((prev) => ({ ...prev, displayOrder: event.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="categoryImageUploadInput">
              <Form.Label>Category Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const file = (event.target as HTMLInputElement).files?.[0] || null;
                  setImageFile(file);
                }}
              />
              {editingCategory?.imageUrl ? (
                <div className="mt-2">
                  <small className="text-muted d-block mb-1">Current image</small>
                  <img
                    src={resolveCategoryImage(editingCategory.imageUrl)}
                    alt={editingCategory.name}
                    style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }}
                  />
                </div>
              ) : null}
            </Form.Group>

            <Form.Check
              className="mb-3"
              type="checkbox"
              id="categoryShowOnHome"
              label="Show this category on Home page"
              checked={form.showOnHome}
              onChange={(event) => setForm((prev) => ({ ...prev, showOnHome: event.target.checked }))}
            />

            <div className="d-flex justify-content-end gap-2">
              <Button type="button" variant="secondary" onClick={() => { setShowDialog(false); resetDialogState(); }}>
                Cancel
              </Button>
              <Button type="submit" variant="dark" data-testid="category-save-btn">
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={!!confirmAction} onHide={closeImpactDialog}>
        <Modal.Header closeButton>
          <Modal.Title>
            {confirmAction === 'delete'
              ? `Delete ${confirmCategory?.name || 'Category'}?`
              : `${confirmTargetStatus ? 'Activate' : 'Deactivate'} ${confirmCategory?.name || 'Category'}?`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmLoading ? (
            <p className="mb-0">Loading impact details...</p>
          ) : (
            <>
              <p>
                {confirmAction === 'delete'
                  ? 'This will delete the category and all related products.'
                  : `This will ${confirmTargetStatus ? 'activate' : 'deactivate'} all products in this category.`}
              </p>
              <p className="mb-2"><strong>Affected products:</strong> {impact?.productCount ?? 0}</p>
              <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                {impact?.products?.length ? (
                  <ul className="mb-0 ps-3">
                    {impact.products.map((product) => (
                      <li key={product._id}>{product.name}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mb-0 text-muted">No related products.</p>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeImpactDialog}>Cancel</Button>
          <Button
            variant={confirmAction === 'delete' ? 'danger' : (confirmTargetStatus ? 'success' : 'warning')}
            onClick={confirmAction === 'delete' ? () => handleDelete(confirmCategory as Category) : handleStatusChange}
            disabled={confirmLoading}
          >
            {confirmAction === 'delete'
              ? 'Confirm Delete'
              : `Confirm ${confirmTargetStatus ? 'Activate' : 'Deactivate'}`}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Categories;
